import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Log error to Sentry if configured
    let eventId: string | null = null;
    if (this.isSentryConfigured()) {
      eventId = Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          section: 'error_boundary',
        },
      });
    }

    this.setState({
      error,
      errorInfo,
      eventId,
    });
  }

  private isSentryConfigured(): boolean {
    return !!(
      import.meta.env.VITE_SENTRY_DSN || 
      import.meta.env.VITE_SENTRY_ORG || 
      import.meta.env.VITE_SENTRY_PROJECT
    );
  }

  private isConvexError(error: Error): boolean {
    return (
      error.message.includes('CONVEX') ||
      error.message.includes('[Request ID:') ||
      error.stack?.includes('convex') ||
      false
    );
  }

  private getErrorTitle(): string {
    const { error } = this.state;
    if (!error) return 'Something went wrong';
    
    if (this.isConvexError(error)) {
      return 'Database Connection Error';
    }
    
    if (error.name === 'ChunkLoadError') {
      return 'Application Update Available';
    }
    
    return 'Unexpected Error';
  }

  private getErrorDescription(): string {
    const { error } = this.state;
    if (!error) return 'An unexpected error occurred.';
    
    if (this.isConvexError(error)) {
      return 'We\'re having trouble connecting to our database. This is usually temporary and should resolve shortly.';
    }
    
    if (error.name === 'ChunkLoadError') {
      return 'The application has been updated. Please refresh the page to get the latest version.';
    }
    
    return 'An unexpected error occurred while loading this page. Our team has been notified and is working on a fix.';
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  private handleReportBug = () => {
    if (this.state.eventId && this.isSentryConfigured()) {
      // Open Sentry user feedback dialog if available
      Sentry.showReportDialog({ eventId: this.state.eventId });
    } else {
      // Fallback to mailto
      const subject = encodeURIComponent('Error Report - dn.place');
      const body = encodeURIComponent(
        `Error: ${this.state.error?.message || 'Unknown error'}\n\n` +
        `Stack Trace:\n${this.state.error?.stack || 'Not available'}\n\n` +
        `Component Stack:\n${this.state.errorInfo?.componentStack || 'Not available'}\n\n` +
        `User Agent: ${navigator.userAgent}\n` +
        `URL: ${window.location.href}\n` +
        `Timestamp: ${new Date().toISOString()}`
      );
      window.open(`mailto:support@dn.place?subject=${subject}&body=${body}`);
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <CardTitle className="text-xl font-semibold text-gray-900">
                {this.getErrorTitle()}
              </CardTitle>
              <CardDescription className="text-gray-600 mt-2">
                {this.getErrorDescription()}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Primary Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={this.handleReload} 
                  className="flex items-center gap-2 flex-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reload Page
                </Button>
                <Button 
                  onClick={this.handleGoHome} 
                  variant="outline" 
                  className="flex items-center gap-2 flex-1"
                >
                  <Home className="w-4 h-4" />
                  Go to Dashboard
                </Button>
              </div>

              {/* Secondary Actions */}
              <div className="pt-4 border-t border-gray-200">
                <Button 
                  onClick={this.handleReportBug} 
                  variant="ghost" 
                  size="sm"
                  className="flex items-center gap-2 w-full text-gray-600 hover:text-gray-900"
                >
                  <Bug className="w-4 h-4" />
                  Report this issue
                </Button>
              </div>

              {/* Development Info */}
              {import.meta.env.DEV && this.state.error && (
                <details className="mt-6 p-4 bg-gray-100 rounded-lg">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                    Error Details (Development)
                  </summary>
                  <div className="space-y-2 text-xs">
                    <div>
                      <strong>Error:</strong>
                      <pre className="mt-1 p-2 bg-white rounded border text-red-600 whitespace-pre-wrap">
                        {this.state.error.message}
                      </pre>
                    </div>
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="mt-1 p-2 bg-white rounded border text-gray-600 whitespace-pre-wrap text-xs overflow-x-auto">
                        {this.state.error.stack}
                      </pre>
                    </div>
                    {this.state.eventId && (
                      <div>
                        <strong>Sentry Event ID:</strong>
                        <code className="ml-2 px-2 py-1 bg-white rounded border">
                          {this.state.eventId}
                        </code>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
